import { useEffect, useState, useCallback } from "react";
import { api, errMsg, today, addDays, fmtDate, formatHours, prettyDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Zap, Trash2, CheckCircle2, Users, ChevronLeft, ChevronRight, Edit, StickyNote, Download, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import CapacityBar from "@/components/CapacityBar";
import AddQuickDialog from "@/components/AddQuickDialog";
import { generateAgendaPDF } from "@/lib/pdf";

const COLS_KEY = "quick_cols";
const COLS_OPTIONS = [1, 2, 3, 4];
const DAYS_BY_COLS = { 1: 7, 2: 14, 3: 21, 4: 28 };
const GRID_CLASS_BY_COLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export default function QuickRepairs() {
  const [mechanics, setMechanics] = useState([]);
  const [agendaStart, setAgendaStart] = useState(new Date());
  const [agenda, setAgenda] = useState({});
  const [capacity, setCapacity] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [workshopName, setWorkshopName] = useState("");
  const [cols, setCols] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(COLS_KEY) || "1", 10);
      return COLS_OPTIONS.includes(v) ? v : 1;
    } catch { return 1; }
  });
  const range = DAYS_BY_COLS[cols];
  const compact = cols > 1;
  const gridClass = GRID_CLASS_BY_COLS[cols];

  useEffect(() => {
    try { localStorage.setItem(COLS_KEY, String(cols)); } catch { /* ignore */ }
  }, [cols]);

  const zoomOut = () => {
    const idx = COLS_OPTIONS.indexOf(cols);
    if (idx < COLS_OPTIONS.length - 1) setCols(COLS_OPTIONS[idx + 1]);
  };
  const zoomIn = () => {
    const idx = COLS_OPTIONS.indexOf(cols);
    if (idx > 0) setCols(COLS_OPTIONS[idx - 1]);
  };

  const load = useCallback(async () => {
    const from = fmtDate(agendaStart);
    const to = fmtDate(addDays(agendaStart, range - 1));
    const [mechRes, agendaRes, capRes, meRes] = await Promise.all([
      api.get("/mechanics?role=quick"),
      api.get(`/quick-agenda?from_date=${from}&to_date=${to}`),
      api.get(`/quick-capacity?from_date=${from}&to_date=${to}`),
      api.get("/workshop/me"),
    ]);
    setMechanics(mechRes.data);
    setAgenda(agendaRes.data);
    setCapacity(capRes.data);
    setWorkshopName(meRes.data.name);
  }, [agendaStart, range]);

  useEffect(() => { load(); }, [load]);

  const downloadPDF = () => {
    if (!capacity) return;
    generateAgendaPDF({
      workshopName,
      title: "Agenda Semanal · Mecánica Rápida",
      days: capacity.days,
      agendaByDate: agenda,
      kind: "quick",
    });
    toast.success("PDF generado");
  };

  const openNewOnDate = (d) => {
    if (d.is_holiday) { toast.error("Ese día es festivo"); return; }
    if (!d.is_workday) { toast.error("Ese día no es laborable"); return; }
    if (d.available <= 0) { toast.error("Ese día está lleno"); return; }
    setNewDate(d.date);
    setNewOpen(true);
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar?")) return;
    try {
      await api.delete(`/quick-repairs/${id}`);
      toast.success("Eliminada");
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  const complete = async (id) => {
    try {
      await api.put(`/quick-repairs/${id}/complete`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo completar"));
    }
  };

  const days = capacity?.days || [];

  return (
    <div className="space-y-6" data-testid="quick-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-3">
            <Zap className="w-8 h-8 text-amber-500" />
            Mecánica Rápida
          </h1>
          <p className="text-slate-500 mt-1">Agenda independiente con su propia regla ajustable.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadPDF} className="h-12" data-testid="download-quick-pdf-btn">
            <Download className="w-4 h-4 mr-2" /> Descargar PDF
          </Button>
          <Button onClick={() => { setNewDate(null); setNewOpen(true); }} className="h-12 bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-quick-button">
            <Zap className="w-4 h-4 mr-2" /> Nueva Rápida
          </Button>
        </div>
      </header>

      {/* Quick mechanics roster */}
      <Card data-testid="quick-mechanics-card">
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-600" /> Mecánicos de Mecánica Rápida
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mechanics.length === 0 ? (
            <div className="text-slate-500 text-sm" data-testid="no-quick-mechanics">
              No hay mecánicos asignados a Mecánica Rápida.{" "}
              <Link to="/mecanicos" className="text-blue-700 font-medium hover:underline">Añadir ahora →</Link>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {mechanics.map((m) => (
                <div key={m.id} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid={`quick-mech-chip-${m.id}`}>
                  <Zap className="w-4 h-4 text-amber-600" />
                  <span className="font-medium text-slate-900">{m.name}</span>
                  <span className="text-xs text-slate-500">· {m.daily_hours}h/día</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick agenda 7-day view */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h2 className="font-display text-2xl font-bold text-slate-900">Agenda Rápida <span className="text-sm text-slate-500 font-normal">· {cols === 1 ? "un día por fila" : `${cols} por fila`} · {range} días</span></h2>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1" data-testid="quick-zoom-toggle">
              <button
                onClick={zoomOut}
                disabled={cols >= COLS_OPTIONS[COLS_OPTIONS.length - 1]}
                className="h-9 w-9 rounded-md inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                data-testid="quick-zoom-out-btn"
                title="Ver más días por fila"
                aria-label="Ver más días por fila"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="px-2 min-w-[52px] text-center text-sm font-medium text-slate-700 self-center tabular-nums" data-testid="quick-days-range-label">
                {cols}×
              </span>
              <button
                onClick={zoomIn}
                disabled={cols <= COLS_OPTIONS[0]}
                className="h-9 w-9 rounded-md inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
                data-testid="quick-zoom-in-btn"
                title="Ver menos días por fila"
                aria-label="Ver menos días por fila"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>
            <Button variant="outline" onClick={() => setAgendaStart((d) => addDays(d, -range))} className="h-10" data-testid="quick-agenda-prev">
              <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
            </Button>
            <Button variant="outline" onClick={() => setAgendaStart(new Date())} className="h-10" data-testid="quick-agenda-today">Hoy</Button>
            <Button variant="outline" onClick={() => setAgendaStart((d) => addDays(d, range))} className="h-10" data-testid="quick-agenda-next">
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>

        <div className={`grid ${gridClass} gap-3`} data-testid="quick-agenda-grid">
          {days.map((d) => {
            const items = agenda[d.date] || [];
            const isToday = d.date === today();
            const isFull = d.is_workday && !d.is_holiday && d.available <= 0 && d.schedulable > 0;
            return (
              <Card key={d.date} data-testid={`quick-agenda-day-${d.date}`} className={
                isToday ? "border-amber-500 border-2" :
                d.is_holiday ? "border-red-200 bg-red-50" :
                isFull ? "border-red-300" : ""
              }>
                <CardHeader className={compact ? "pb-1 pt-2 px-3" : "pb-2"}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <CardTitle className={compact ? "font-display text-base" : "font-display text-lg"}>
                      {prettyDate(d.date)}
                      {isToday && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded ml-2">Hoy</span>}
                      {d.is_holiday && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded ml-2">Festivo{d.holiday_label ? ` · ${d.holiday_label}` : ""}</span>}
                      {isFull && <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded ml-2">Lleno</span>}
                    </CardTitle>
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-slate-500">
                        {d.is_holiday ? "No se programa" : d.is_workday ? (<><span className="font-semibold text-slate-900">{formatHours(d.used)}</span> / {formatHours(d.schedulable)}</>) : "No laborable"}
                      </div>
                      {d.is_workday && !d.is_holiday && d.total_mechanic_hours > 0 && (
                        <Button
                          size="sm"
                          onClick={() => openNewOnDate(d)}
                          disabled={d.available <= 0}
                          className={d.available > 0 ? "bg-amber-500 hover:bg-amber-600 h-9" : "bg-slate-300 h-9"}
                          data-testid={`add-quick-${d.date}`}
                        >
                          <span className="text-lg mr-1">+</span> {d.available > 0 ? "Nueva" : "Lleno"}
                        </Button>
                      )}
                    </div>
                  </div>
                  {d.is_workday && !d.is_holiday && d.total_mechanic_hours > 0 && (
                    <div className="mt-2">
                      <CapacityBar used={d.used} schedulable={d.schedulable} total={d.total_mechanic_hours} label="" />
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  {items.length === 0 ? (
                    <div className="text-sm text-slate-400 italic">{d.is_holiday ? "Día festivo" : "Sin tareas rápidas"}</div>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {items.map((it) => (
                        <li key={it.id} className="py-3" data-testid={`quick-agenda-item-${it.id}`}>
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                {it.license_plate && (
                                  <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded text-sm">{it.license_plate}</span>
                                )}
                                {(it.brand || it.model) && (
                                  <span className="text-sm text-slate-700 font-medium">{it.brand} {it.model}</span>
                                )}
                                {it.status === "completed" && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">Hecha</span>
                                )}
                              </div>
                              <div className="text-slate-900 font-medium break-words">{it.description}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                {it.customer_name && <span>{it.customer_name}</span>}
                                {it.phone && <span className="ml-2">· {it.phone}</span>}
                              </div>
                              {it.notes && (
                                <div className="mt-2 flex items-start gap-1.5 text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded px-2 py-1.5" data-testid={`quick-notes-${it.id}`}>
                                  <StickyNote className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                                  <span className="min-w-0 flex-1 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{it.notes}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                              <span className="text-2xl font-display font-bold text-slate-900">{formatHours(it.hours)}</span>
                              <div className="flex items-center gap-1">
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-600 hover:bg-slate-100" onClick={() => setEditTarget(it)} data-testid={`edit-quick-${it.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {it.status !== "completed" && (
                                  <Button variant="ghost" size="icon" className="h-9 w-9 text-green-600" onClick={() => complete(it.id)} data-testid={`complete-quick-${it.id}`}>
                                    <CheckCircle2 className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="icon" className="h-9 w-9 text-red-600" onClick={() => remove(it.id)} data-testid={`delete-quick-${it.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Edit dialog */}
      <AddQuickDialog
        initial={editTarget}
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); }}
      />

      {/* Create dialog */}
      <AddQuickDialog
        open={newOpen}
        onOpenChange={(o) => { setNewOpen(o); if (!o) setNewDate(null); }}
        initialDate={newDate}
        onCreated={() => { setNewOpen(false); setNewDate(null); load(); }}
      />
    </div>
  );
}
